import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonLoading,
  IonPage,
  IonRow,
  IonToast
} from "@ionic/react";
import Header from "../components/Header";
import {ROLE} from "../../enums/ROLE";
import {AuthContext} from "../../providers/authContext";
import {bookOutline} from "ionicons/icons";
import yoneticiService from "../../services/YoneticiService";
import {formatDateToDDMMYYYY, logError, parseTurkishNumber} from "../../utils";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {SelectItem} from "primereact/selectitem";
import {Dropdown} from "primereact/dropdown";
import "./YoneticiTahsilatGozlem.css";
import {ColumnGroup} from "primereact/columngroup";
import {Row} from "primereact/row";
import {Calendar, CalendarValueType} from "primereact/calendar";
import {BankaBakiyeTable} from "../entities/BankaBakiyeTable";

const YoneticiBankaBakiye: React.FC = () => {
  const { kurumBilgileri, logo } = React.useContext(AuthContext);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [showToastMessage, setShowToastMessage] = useState<string>();
  const [startDate, setStartDate] = useState<CalendarValueType>();
  const [endDate, setEndDate] = useState<CalendarValueType>();
  const [bankaBakiyeListesi, setBankaBakiyeListesi] = useState<BankaBakiyeTable[]>([]);
  const [selectedBanka, setSelectedBanka] = useState(null);
  const [bankaSelectItems, setBankaSelectItems] = useState<SelectItem[]>([]);
  const [filtrelenmisBankaVerisi, setFiltrelenmisBankaVerisi] = useState(bankaBakiyeListesi || []);
  const bankaBakiyeTable = useRef<DataTable>(null);

  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear()+"-01-01");
    setStartDate(firstDay);
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    setEndDate(endDate);
    fetchData(firstDay, endDate);
  }, []);

  useEffect(() => {
    yoneticiService.getBankaListesi()
      .then((data: string[]) => {
        const items: SelectItem[] = data.map(t => ({
          label: t,
          value: t
        }));
        setBankaSelectItems(items);
      })
      .catch((error) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      });
  }, []);

  const fetchData = (startDate: CalendarValueType, endDate: CalendarValueType) => {
    if (!startDate || !endDate) {
      setShowToastMessage("Başlangıç ve Bitiş Tarihlerini giriniz");
      return
    };
    setShowLoading(true);
    yoneticiService
      .getBankaBakiyeDurumu(formatDateToDDMMYYYY(startDate as Date), formatDateToDDMMYYYY(endDate as Date))
      .then((data: BankaBakiyeTable[]) => {
        setBankaBakiyeListesi(data);
        setFiltrelenmisBankaVerisi(data);
      })
      .catch((error: any) => {
        logError(error);
        setShowToastMessage(
          error.response ? error.response.data.message : error
        );
      })
      .finally(() => setShowLoading(false));
  }

  const bankaFilter = <Dropdown value={selectedBanka} options={bankaSelectItems}
                                          onChange={(e) => {
                                            bankaBakiyeTable.current?.filter(e.value, 'bankaAdi', 'equals');
                                            setSelectedBanka(e.value);
                                        }} placeholder="Hepsi"  className="p-column-filter" showClear style={{ width: '9rem' }} />

  const bakiyeToplam = useMemo(() => {
    const veri = filtrelenmisBankaVerisi ?? bankaBakiyeListesi ?? [];
    return veri.reduce((toplam, item) => toplam + (parseTurkishNumber(item.bakiye) || 0), 0);
  }, [filtrelenmisBankaVerisi, bankaBakiyeListesi]);

  const tahsilatFooterGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="TOPLAM" footerStyle={{ textAlign: 'right' }} />
        <Column footer={bakiyeToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} footerStyle={{ textAlign: 'right' }} />
      </Row>
    </ColumnGroup>
  );

  return (
    <IonPage>
      <Header
        title={kurumBilgileri?.kisaTanim}
        subtitle=""
        logo={logo}
        roles={[ROLE.Yonetici]}
      />
      <IonContent>
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={"Lütfen Bekleyin..."}
        />
        <IonToast
          isOpen={showToastMessage !== undefined}
          onDidDismiss={() => setShowToastMessage(undefined)}
          message={showToastMessage}
          duration={5000}
          position="top"
        />
        <IonCard>
          <IonCardHeader>
            <IonItem color="light">
              <IonGrid>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="2"><IonLabel>Başlangıç Tarihi	</IonLabel></IonCol>
                  <IonCol size="5" sizeSm="3">
                    <Calendar id="startDate" locale="tr"
                              dateFormat="dd.mm.yy"
                              value={startDate}
                              onChange={(e) => setStartDate(e.value)} showIcon />
                  </IonCol>
                </IonRow>
                <IonRow className="ion-align-items-center">
                  <IonCol size="5" sizeSm="2"><IonLabel>Bitiş Tarihi	</IonLabel></IonCol>
                  <IonCol size="5" sizeSm="3">
                    <Calendar id="endDate"
                              dateFormat="dd.mm.yy"
                              value={endDate}
                              onChange={(e) => setEndDate(e.value)} showIcon />
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol size="10" sizeSm="5">
                    <IonButton expand="block" onClick={() => fetchData(startDate, endDate)}>Sorgula</IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonItem>
            <IonItem color="light">
              <IonIcon icon={bookOutline} slot="start" />
              Banka Bakiye
            </IonItem>
          </IonCardHeader>
          <IonCardContent>
            <DataTable ref={bankaBakiyeTable} value={bankaBakiyeListesi}
                       className="p-datatable-sm tahsilat-table"
                       footerColumnGroup={tahsilatFooterGroup}
                       onValueChange={(e) => setFiltrelenmisBankaVerisi(e)}
                       stripedRows filterDisplay="row" showGridlines
                       scrollable
                       scrollDirection="horizontal"
                       emptyMessage="Tabloda herhangi bir veri mevcut değil">
              <Column field="bankaAdi" header="BANKA ADI" headerClassName="ion-justify-content-center"
                      filter
                      filterElement={bankaFilter}
                      showFilterMenu={false}
                      showFilterMatchModes={false}
                      showClearButton={false}
                      headerStyle={{ width: '75%' }} bodyStyle={{ width: '75%' }} />
              <Column field="bakiye" header="BAKİYE" bodyClassName="ion-justify-content-end" headerClassName="ion-justify-content-center"
                      headerStyle={{ width: '25%' }} bodyStyle={{ width: '25%' }}/>
            </DataTable>

          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  )
};

export default YoneticiBankaBakiye;
